/**
 * The client's running Smart Portfolio investments, each in its own sub-account: their money is added to
 * the balance and equity the same way a master-managed account's is (stores/managed.ts). Kept live via the
 * user:<accountId> channel of each investment's account. Only for the live account: demo has none.
 */
import { useEffect, useMemo } from 'react';
import type { InvestmentAccount, InvestmentBook, UserEvent } from '@platform/shared';
import { api } from '../lib/api';
import { createStore, useStore } from '../lib/store';
import { ws } from '../lib/ws';
import { useSubAccountTotals, type ManagedTotals } from './managed';
import { useActiveAccount } from './session';

interface InvestmentsState {
  liveId: string | null;
  accounts: InvestmentAccount[];
  positions: InvestmentBook['positions'];
}

const EMPTY: InvestmentsState = { liveId: null, accounts: [], positions: [] };
export const investmentsStore = createStore<InvestmentsState>(EMPTY);

export async function reloadInvestments() {
  const liveId = investmentsStore.get().liveId;
  if (!liveId) return;
  try {
    const b = await api.get<InvestmentBook>('/api/portfolio-investments/accounts');
    if (investmentsStore.get().liveId === liveId) investmentsStore.set({ accounts: b.accounts, positions: b.positions });
  } catch {
    /* keep what we have; the next resync retries */
  }
}

function onInvestmentEvent(ev: UserEvent) {
  if (ev.type === 'balance') {
    investmentsStore.set((s) => ({ accounts: s.accounts.map((a) => (a.account.id === ev.account.id ? { ...a, account: ev.account } : a)) }));
  } else if (ev.type === 'position') {
    investmentsStore.set((s) => {
      if (!s.accounts.some((a) => a.account.id === ev.position.accountId)) return {};
      const p = { ...ev.position, liqPrice: null };
      const rest = s.positions.filter((x) => x.id !== p.id);
      return { positions: p.status === 'open' ? [p, ...rest] : rest };
    });
  }
}

/** Mount once: follows the live account and keeps its investments' accounts live. */
export function useInvestmentsSync() {
  const acc = useActiveAccount();
  const liveId = acc?.kind === 'live' ? acc.id : null;
  const accountIds = useStore(investmentsStore, (s) => s.accounts.map((a) => a.account.id).join(','));
  useEffect(() => {
    investmentsStore.set({ ...EMPTY, liveId });
    if (!liveId) return;
    void reloadInvestments();
    // The Smart Portfolio screens reload after the client's own changes; a close by the stop loss,
    // staff or a retirement comes with a notification.
    const unsub = ws.subscribe(`user:${liveId}`, (ev: UserEvent) => {
      if (ev.type === 'notification') void reloadInvestments();
    });
    const t = window.setInterval(() => void reloadInvestments(), 60_000);
    return () => {
      unsub();
      window.clearInterval(t);
    };
  }, [liveId]);
  useEffect(() => {
    if (!accountIds) return;
    const unsubs = accountIds.split(',').map((id) => ws.subscribe(`user:${id}`, onInvestmentEvent));
    return () => unsubs.forEach((u) => u());
  }, [accountIds]);
}

export const useInvestmentAccounts = () => useStore(investmentsStore, (s) => s.accounts);

/** Totals of the running investments, re-marked live; null when the client has none. */
export function useInvestmentTotals(): ManagedTotals | null {
  const accounts = useInvestmentAccounts();
  const positions = useStore(investmentsStore, (s) => s.positions);
  const subs = useMemo(() => accounts.map((a) => ({ account: a.account, invested: a.investment.invested, dayStartEquity: a.dayStartEquity })), [accounts]);
  return useSubAccountTotals(subs, positions);
}
